import React, {useEffect, useRef, useState} from 'react'
import {Row, Col} from 'react-bootstrap'
import {FolderTreeView} from './FolderContent'
import {FileDetailsView} from './FolderTreeStructure'
import {StorageDropdown} from './StorageDropdown'
import {getFoldersLevel1AndLevel2Api, getRecordsFolder} from '../../core/_requests'
import {useAuth} from '../../../auth'
import {toast} from 'react-toastify'
import {useParams} from 'react-router-dom'
// import { RecordKeeperCreateLinkDrawer } from './RecordKeeperCreateLinkDrawer'

// type FolderContent = {[key: string]: FolderContent | {type: string}}

type Folder = {
  id: string
  type: string
  currentFolderLevel: string
  nextFolderLevel: string
  isExpanded: boolean
  children: Record<string, Folder> // Recursive structure for children
  year?: number | null
  quarter?: number | undefined | null
  month?: number | undefined | null
}

type FolderStructure = {
  [key: string]: Folder
}

const FileManagerRecords = () => {
  const {companyId} = useAuth()
  const [isFileStructureLoading, setIsFileStructureLoading] = useState<boolean>(false)
  const [selectedPath, setSelectedPath] = useState<string[]>([])
  const [selectedFolderName, setSelectedFolderName] = useState<string>('')
  const [selectedFolders, setSelectedFolders] = useState<any>([])
  const [loading, setLoading] = useState(false)
  const selectedFoldersData = useRef<any>([])
  const [selectedStorage, setSelectedStorage] = useState<string[]>([])
  let optionalParam = useParams()['*']?.split('/') || []
  optionalParam[2] =
    optionalParam.length > 2 && optionalParam[2] && optionalParam[2].includes('Not Known')
      ? 'Not Known(Yearly)'
      : optionalParam[2]
  optionalParam[3] =
    optionalParam.length > 3 && optionalParam[3] && optionalParam[3].includes('Not Known')
      ? 'Not Known(Quarterly)'
      : optionalParam[3]
  optionalParam[4] =
    optionalParam.length > 4 && optionalParam[4] && optionalParam[4].includes('Not Known')
      ? 'Not Known (Monthly)'
      : optionalParam[4]
  const [processedParams, setProcessedParams] = useState<string[]>([])
  const currentFolderStructure = useRef<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [folderStructure, setFolderStructure] = useState<any>({})
  const [localExpanded, setLocalExpanded] = useState<Record<string, boolean>>({}) // Track expanded state locally
  const folderTrackingRef = useRef<any>(null)
  const [isPathFromOutside, setIsPathFromOutside] = useState(true)
  const [isFilePathLoading, setIsFilePathLoading] = useState(false)
  const [isFolderClicked, setIsFolderClicked] = useState(false)

  const getFolderOrFileList = async (
    folderLevel: string,
    folderId?: string,
    isRenameTriggered?: boolean
  ) => {
    if (!companyId) return

    try {
      setIsFileStructureLoading(true)

      const {
        data: {data, success, errors},
      } = await getRecordsFolder(
        companyId,
        selectedStorage,
        folderLevel,
        folderId ? folderId : undefined,
      )

      if (success) {
        if (isRenameTriggered) {
          setSelectedPath([])
          setSelectedFolderName('')
        }
        if (folderId) {
          const transformedChildren: Record<string, any> = {}

          // Example: Data Transformation Logic
          data.forEach((item: any) => {
            const folderName = item.name

            if (!transformedChildren[folderName]) {
              transformedChildren[folderName] = {
                id: item.id,
                type: item.type,
                name: item.type === 'file' ? item.name.split('.')[0] : item.name,
                fileVersionName: item.type === 'file' ? item.fileVersionName : null,
                fileVersionId: item.type === 'file' ? item.fileVersionId : null,
                currentFolderLevel: item.currentFolderLevel,
                nextFolderLevel: item.nextFolderLevel || 'LevelTwo',
                isExpanded: false,
                children: {}, // Prepare for nested children
                uuid: item.uuid,
                numberOfFiles: item.numberOfFiles,
              }
            }
          })

          // Update the folder structure
          // const updatedFolderStructure = updateFolderStructure(
          //   folderStructure,
          //   folderId,
          //   transformedChildren
          // )

          // Update folderTrackingRef with new children
          if (folderTrackingRef.current) {
            const updateTrackingRef = (structure: Record<string, any>, targetFolderId: string) => {
              Object.entries(structure).forEach(([key, folder]) => {
                if (transformedChildren) {
                  currentFolderStructure.current = {
                    ...currentFolderStructure.current,
                    ...transformedChildren,
                  }
                }

                if (
                  (folder.id === targetFolderId && folder.nextFolderLevel === folderLevel) ||
                  folder.type === 'file'
                ) {
                  folder.children = {
                    ...folder.children,
                    ...transformedChildren,
                  }
                } else if (folder.children && typeof folder.children === 'object') {
                  updateTrackingRef(folder.children, targetFolderId)
                }
              })
            }
            updateTrackingRef(folderTrackingRef.current, folderId)
          }

          setFolderStructure(folderTrackingRef.current)
        }
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setIsFileStructureLoading(false)
    }
  }

  const traverseAndUpdateFolderStructure = async (
    folderStructure: FolderStructure,
    folderNames: string[],
    index: number = 0
  ) => {
    if (index >= folderNames.length) return
    setIsFolderClicked(false)
    folderNames = folderNames.filter((folderName) => folderName !== undefined && folderName !== '')

    const folderName = folderNames[index]
    const folder = folderStructure[folderName]

    if (!folder) {
      setIsFilePathLoading(false)
      return
    }

    selectedFoldersData.current = [...selectedFoldersData.current, folder]

    if (folder) {
      // Fetch children for the current folder using its ID
      await handleDoubleFolderClick([...selectedPath, `${folderName}$-@${folder.id}`], folder)
      if (currentFolderStructure.current) {
        setSelectedFolderName(folderName)

        if (
          !currentFolderStructure.current[folderNames[index + 1]] &&
          currentFolderStructure.current[folderNames[index + 1]] !== undefined
        ) {
          console.error(
            `The folder name "${
              folderNames[index + 1]
            }" provided in the URL is incorrect. Please give the correct folder path`
          )
          toast.error(
            `The folder name "${
              folderNames[index + 1]
            }" provided in the URL is incorrect. Please give the correct folder path`
          )
          return
        }


        if (
          (folderNames[index + 1] &&
            currentFolderStructure.current &&
            currentFolderStructure.current[folderNames[index + 1]] &&
            Object.keys(currentFolderStructure.current[folderNames[index + 1]]).length > 0 &&
            Object.keys(currentFolderStructure.current[folderNames[index + 1]].children).length ===
              0 &&
            index + 1 <= folderNames.length) ||
          folderNames.length === 1
        ) {
          await traverseAndUpdateFolderStructure(
            currentFolderStructure.current,
            folderNames,
            index + 1
          )

          if (folderNames.length > 1) {
            setSelectedPath((prevSelectedPath) => {
              const newPath = [`${folderName}$-@${folder.id}`, ...prevSelectedPath] // Add folderName at the beginning (reverse order)
              return newPath
            })
          }

          setLocalExpanded((prevExpanded) => {
            return {
              ...prevExpanded,
              [`${folderName}$-@${folder.id}`]: true, // Set the current folder as expanded
            }
          })

          setIsFilePathLoading(false)
        }
      }
    }
  }

  async function getFoldersLevel1AndLevel2() {
    if (!companyId) return
    try {
      setIsLoading(true)
      setLoading(true)
      const {
        data: {data, success, errors},
      } = await getFoldersLevel1AndLevel2Api(companyId)

      if (success) {
        setFolderStructure(data.updatedFolderStructure)
        folderTrackingRef.current = data.updatedFolderStructure
        if (isFolderClicked) {
          setSelectedFolders(data.foldersForLevel1) //I made changes here this was old line : setSelectedFolders(data.foldersForLevel1)
        }
      } else {
        toast.error(errors.name)
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setIsLoading(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (Object.keys(folderStructure).length === 0) {
      getFoldersLevel1AndLevel2()
    }
  }, [selectedStorage]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (Object.keys(folderStructure).length > 0 && isPathFromOutside) {
      if (optionalParam.length > 0 && processedParams.length !== optionalParam.length) {

        setIsFilePathLoading(true)

        setProcessedParams(optionalParam)

        traverseAndUpdateFolderStructure(folderStructure, optionalParam)
      }
    }
  }, [folderStructure, optionalParam]) // eslint-disable-line react-hooks/exhaustive-deps


  const handleFolderClick = async (
    folderId: string,
    folderLevel: string,
  ) => {
    await getFolderOrFileList(folderLevel, folderId)
  }

  const handleDoubleFolderClick = async (
    path: string[],
    folderContent: any,
    selectedFolderNameFromPath?: string,
    isBreadCrumbClick?: boolean,
    isClicked?: boolean,
    isDoubleClickFromFileExplorer?: boolean
  ) => {
    setLoading(true)

    if (isBreadCrumbClick) {
      selectedFoldersData.current = selectedFoldersData.current.filter(
        (item: any) => item !== undefined
      )

      if (selectedFoldersData.current) {
        const indexToRemove = selectedFoldersData.current.findIndex(
          (folder: any) => folder.name === folderContent.name
        )

        if (indexToRemove !== -1) {
          selectedFoldersData.current = selectedFoldersData.current.slice(0, indexToRemove + 1)
        }
      }
    }

    await getFolderOrFileList(
      folderContent.currentFolderLevel === 'Level1' ? 'LevelOne' : folderContent?.nextFolderLevel,
      folderContent.currentFolderLevel === 'Level1' ? undefined : folderContent?.id,
     
    )

    if (selectedFolderNameFromPath?.split('$-@')[0]) {
      setSelectedFolderName(selectedFolderNameFromPath.split('$-@')[0])
      const selectedPathIndex = path.indexOf(selectedFolderNameFromPath)

      setSelectedPath(
        selectedPath.length > 0 && path.length > 1
          ? selectedPath.slice(0, selectedPathIndex + 1)
          : path
      )
    } else {
      setSelectedFolderName(path[path.length - 1])
      setSelectedPath(path.filter((pathValue) => pathValue.length > 0))
    }
    try {
      const {
        data: {data, success, errors},
      } = await getRecordsFolder(
        companyId!,
        selectedStorage,
        folderContent?.nextFolderLevel,
        folderContent?.id,
        true
      )

      if (success) {
        setSelectedFolders(data)
        if (isClicked) {
          const url = new URL(window.location.href)
          

          // Handle breadcrumb click case
          if (selectedFolderNameFromPath && (isBreadCrumbClick || isDoubleClickFromFileExplorer)) {
            
            const selectedPathIndex = path.map((pathValue) => pathValue.split('$-@')[0]).indexOf(selectedFolderNameFromPath)
            const newPathSegments = path.slice(0, selectedPathIndex + 1)
            url.pathname =
              '/rk/records-and-files/' +
              newPathSegments
                .map((newSegment) =>
                  newSegment.split('$-@')[0]
                )
                .map(encodeURIComponent)
                .join('/')
          } else {   
            const segment = folderContent.name
            const encodedSegment = encodeURIComponent(segment)
            url.pathname = url.pathname.endsWith('/')
              ? url.pathname + encodedSegment
              : url.pathname + '/' + encodedSegment
          }

          // Update the URL without refreshing
          window.history.replaceState(null, '', url.toString())
          setIsPathFromOutside(false)
        }
      } else {
        toast.error(errors[0])
      }
    } catch (err) {
      console.error('Error fetching folder or file list:', err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='bg-white rounded-2 p-4 mt-5'>
      <Row>
        <Col md={3} className='border-end p-4 pt-0'>
          <StorageDropdown
            selectedStorage={selectedStorage}
            setSelectedStorage={setSelectedStorage}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
          <h1 className='font-size-16 text-dark lh-base mb-4'>All Departments</h1>
          <div
            className='h-600px'
            style={{overflowX: 'hidden', overflowY: 'scroll', scrollbarWidth: 'none'}}
          >
            <FolderTreeView
              folderStructure={folderStructure}
              setFolderStructure={setFolderStructure}
              selectedPath={selectedPath}
              handleFolderClick={handleFolderClick}
              handleDoubleFolderClick={handleDoubleFolderClick}
              selectedFoldersData={selectedFoldersData}
              isFileStructureLoading={isFileStructureLoading}
              isStorageLoading={isLoading}
              loading={loading}
              setSelectedFolders={setSelectedFolders}
              localExpanded={localExpanded}
              setLocalExpanded={setLocalExpanded}
              getFolderOrFileList={getFoldersLevel1AndLevel2}
              setSelectedPath={setSelectedPath}
            />
          </div>
        </Col>
        <Col
          md={9}
          className='p-4 pt-0 h-800px position-relative'
          style={{overflowX: 'hidden', overflowY: 'scroll', scrollbarWidth: 'none'}}
        >
          <FileDetailsView
            selectedPath={selectedPath}
            selectedFolders={selectedFolders}
            loading={loading}
            selectedFolderName={selectedFolderName}
            handleDoubleFolderClick={handleDoubleFolderClick}
            selectedFoldersData={selectedFoldersData}
            getFolderOrFileList={getFoldersLevel1AndLevel2}
            folderStructure={folderStructure}
            setFolderStructure={setFolderStructure}
            setLocalExpanded={setLocalExpanded}
            setSelectedFolders={setSelectedFolders}
            setSelectedPath={setSelectedPath}
            setSelectedFolderName={setSelectedFolderName}
            isFilePathLoading={isFilePathLoading}
            setIsFolderClicked={setIsFolderClicked}
          />
        </Col>
      </Row>
    </div>
  )
}

export default FileManagerRecords
